import { useEffect, useState } from 'react';

import {
  getSupportSettingsCatalog,
  SupportSettingCatalogItem,
  SupportSettingCategory,
  SupportType,
} from '../../../api/services/RiderSupportSettings';

type CatalogReady = {
  status: 'ready';
  categories: SupportSettingCategory[];
  bySupportType: Record<SupportType, SupportSettingCatalogItem>;
  detailsMaxLength: number;
};

type CatalogState = { status: 'idle' } | { status: 'loading' } | CatalogReady | { status: 'error'; error: unknown };

type FetchResult = CatalogReady | { status: 'idle' };

let catalogPromise: Promise<FetchResult> | null = null;

export const resetCatalogSingleton = () => {
  catalogPromise = null;
};

const buildBySupportType = (categories: SupportSettingCategory[]): Record<SupportType, SupportSettingCatalogItem> => {
  const map: Record<SupportType, SupportSettingCatalogItem> = {};

  for (const category of categories) {
    for (const setting of category.supportSettings) {
      map[setting.supportType] = setting;
    }
  }

  return map;
};

const fetchCatalog = (): Promise<FetchResult> => {
  if (!catalogPromise) {
    catalogPromise = getSupportSettingsCatalog()
      .then((response) => {
        if (response.type === 'error') {
          if (response.status === 404) {
            return { status: 'idle' as const };
          }
          throw response.error;
        }
        const { categories, detailsMaxLength } = response.data;

        return {
          status: 'ready' as const,
          categories,
          bySupportType: buildBySupportType(categories),
          detailsMaxLength,
        };
      })
      .catch((error) => {
        catalogPromise = null;
        throw error;
      });
  }

  return catalogPromise;
};

export const useSupportSettingsCatalog = (): CatalogState => {
  const [state, setState] = useState<CatalogState>({ status: 'loading' });

  useEffect(() => {
    fetchCatalog()
      .then((result) => setState(result))
      .catch((error) => setState({ status: 'error', error }));
  }, []);

  return state;
};
